import type { ComponentType } from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import isNil from "lodash/isNil";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { MetricRangeType } from "@waterly/graphql-types";
import type { MetricRangeInput } from "@waterly/utils-isomorphic";
import { MetricRangeValidationErrorCode, validateMetricRanges } from "@waterly/utils-isomorphic";

import { SeverityColor } from "../SeveritySelect/severity.utils";

import IdealRangeConfig from "./MetricRangeConfig/IdealRangeConfig";
import type { MetricRangeTagOption } from "./MetricRangeConfig/MetricRangeTagSelect";
import { normalizeIdealRangeForMetricRangeType } from "./metricRangeSetupWizard.utils";
import type { MetricRangeTypeSelectProps } from "./MetricRangeTypeSelect";
import MetricRangeTypeSelect from "./MetricRangeTypeSelect";
import type { MetricRangeCardMetricRange } from "./metricRangeUtils";
import { createWarningRange } from "./metricRangeUtils";
import { useMetricRangeErrorsManager } from "./useMetricRangeErrorsManager";

export type MetricRangeSetupWizardProps = {
  cloneMetricRangesComponent?: ComponentType<{ onChange: MetricRangeSetupWizardProps["onChange"] }>;
  metricRangeType: MetricRangeType | null;
  onChange: (value: {
    metricRangeType: MetricRangeType | null;
    metricRanges: MetricRangeCardMetricRange[];
  }) => void;
  metricRangeTagOptions: MetricRangeTagOption[];
  devMode: boolean;
};

type IdealRangeTouchedFields = {
  lowerLimit: boolean;
  upperLimit: boolean;
};

const MetricRangeSetupWizard = ({
  metricRangeType: initialMetricRangeType,
  onChange,
  cloneMetricRangesComponent: CloneMetricRangesComponent,
  metricRangeTagOptions,
  devMode,
}: MetricRangeSetupWizardProps) => {
  const { t } = useTranslation(["common"]);
  const [metricRangeType, setMetricRangeType] = useState<MetricRangeType | null>(initialMetricRangeType);
  const [touchedFields, setTouchedFields] = useState<IdealRangeTouchedFields>({
    lowerLimit: false,
    upperLimit: false,
  });

  const [idealRange, setIdealRange] = useState<MetricRangeCardMetricRange>({
    id: uuid(),
    notificationEnabled: false,
    rangeMinInclusive: true,
    rangeMaxInclusive: true,
    isNew: true,
    rangeMin: null as unknown as number,
    rangeMax: null as unknown as number,
    name: t("common:metric_range_name_ideal"),
    color: SeverityColor.GREEN,
    ideal: true,
  });
  const idealRangeRef = useRef(idealRange);
  const showConfirmButton = metricRangeType !== null;
  const hasSelectedMetricRangeType = metricRangeType !== null;
  const requiresLowerInfinityWarningRange =
    metricRangeType === MetricRangeType.Lower || metricRangeType === MetricRangeType.Both;
  const requiresUpperInfinityWarningRange =
    metricRangeType === MetricRangeType.Upper || metricRangeType === MetricRangeType.Both;

  const handleMetricRangeTypeChange = useCallback<MetricRangeTypeSelectProps["onChange"]>(
    (newMetricRangeType) => {
      setMetricRangeType(newMetricRangeType);
      setIdealRange((previousIdealRange) => {
        const nextIdealRange = normalizeIdealRangeForMetricRangeType(previousIdealRange, newMetricRangeType);
        idealRangeRef.current = nextIdealRange;
        return nextIdealRange;
      });
      setTouchedFields((previousTouchedFields) => ({
        ...previousTouchedFields,
        lowerLimit:
          newMetricRangeType === MetricRangeType.Upper || newMetricRangeType === null
            ? false
            : previousTouchedFields.lowerLimit,
        upperLimit:
          newMetricRangeType === MetricRangeType.Lower || newMetricRangeType === null
            ? false
            : previousTouchedFields.upperLimit,
      }));
    },
    [],
  );

  const handleIdealRangeChange = useCallback((value: MetricRangeCardMetricRange) => {
    idealRangeRef.current = value;
    setIdealRange(value);
  }, []);

  const buildIdealAndWarningRanges = useCallback(
    (draftIdealRange: MetricRangeCardMetricRange) => {
      const ranges: MetricRangeCardMetricRange[] = [draftIdealRange];

      if (requiresLowerInfinityWarningRange) {
        ranges.push(
          createWarningRange({
            name: t("common:warning_low"),
            rangeMinInclusive: false,
            rangeMaxInclusive: false,
            rangeMin: null, //cover -infinity
            rangeMax: draftIdealRange.rangeMin,
          }),
        );
      }

      if (requiresUpperInfinityWarningRange) {
        ranges.push(
          createWarningRange({
            name: t("common:warning_high"),
            rangeMinInclusive: false,
            rangeMaxInclusive: false,
            rangeMin: draftIdealRange.rangeMax,
            rangeMax: null, //cover infinity
          }),
        );
      }
      return ranges;
    },
    [requiresLowerInfinityWarningRange, requiresUpperInfinityWarningRange, t],
  );

  const idealAndWarningRanges = useMemo(
    () => buildIdealAndWarningRanges(idealRange),
    [buildIdealAndWarningRanges, idealRange],
  );

  const { idealMetricRangeErrors, validateBeforeSave } = useMetricRangeErrorsManager(idealAndWarningRanges);

  const validationResult = useMemo(
    () => validateMetricRanges(idealAndWarningRanges as MetricRangeInput[]),
    [idealAndWarningRanges],
  );
  const isValid = validationResult.success;
  const suppressIncompleteBothRangeOverlap =
    metricRangeType === MetricRangeType.Both &&
    validationResult.error?.code === MetricRangeValidationErrorCode.METRIC_RANGE_OVERLAP &&
    (isNil(idealRange.rangeMin) || isNil(idealRange.rangeMax));

  const validateIdealRangeField = useCallback(
    ({
      field,
      side,
      draftIdealRange,
    }: {
      field: keyof IdealRangeTouchedFields;
      side: MetricRangeType.Lower | MetricRangeType.Upper;
      draftIdealRange: MetricRangeCardMetricRange;
    }) => {
      setTouchedFields((previous) => ({ ...previous, [field]: true }));
      validateBeforeSave({
        proposedRanges: buildIdealAndWarningRanges(draftIdealRange),
        editedRangeId: draftIdealRange.id,
        side,
      });
    },
    [buildIdealAndWarningRanges, validateBeforeSave],
  );

  const handleLowerLimitBlur = useCallback(
    (rangeMin: number | null) => {
      validateIdealRangeField({
        field: "lowerLimit",
        side: MetricRangeType.Lower,
        draftIdealRange: { ...idealRangeRef.current, rangeMin: rangeMin as number },
      });
    },
    [validateIdealRangeField],
  );

  const handleUpperLimitBlur = useCallback(
    (rangeMax: number | null) => {
      validateIdealRangeField({
        field: "upperLimit",
        side: MetricRangeType.Upper,
        draftIdealRange: { ...idealRangeRef.current, rangeMax: rangeMax as number },
      });
    },
    [validateIdealRangeField],
  );

  const handleConfirm = useCallback(() => {
    if (!isValid) return;
    onChange({ metricRangeType, metricRanges: idealAndWarningRanges });
  }, [isValid, onChange, metricRangeType, idealAndWarningRanges]);

  return (
    <Stack gap={2}>
      <Box display="flex" justifyContent="right">
        {CloneMetricRangesComponent && <CloneMetricRangesComponent onChange={onChange} />}
      </Box>
      <MetricRangeTypeSelect
        fullWidth
        metricRangeType={metricRangeType}
        onChange={handleMetricRangeTypeChange}
      />
      {hasSelectedMetricRangeType && (
        <IdealRangeConfig
          idealRange={idealRange}
          metricRangeType={metricRangeType}
          metricRangeTagOptions={metricRangeTagOptions}
          onLowerLimitChange={handleIdealRangeChange}
          onUpperLimitChange={handleIdealRangeChange}
          lowerLimitErrorMessage={
            touchedFields.lowerLimit && !suppressIncompleteBothRangeOverlap
              ? idealMetricRangeErrors[MetricRangeType.Lower]
              : undefined
          }
          upperLimitErrorMessage={
            touchedFields.upperLimit && !suppressIncompleteBothRangeOverlap
              ? idealMetricRangeErrors[MetricRangeType.Upper]
              : undefined
          }
          onLowerLimitBlur={handleLowerLimitBlur}
          onUpperLimitBlur={handleUpperLimitBlur}
          allowIncompleteLimits
          devMode={devMode}
        />
      )}
      <Box display="flex">
        <Box flexGrow={1} />
        {showConfirmButton && (
          <Button variant="contained" onClick={handleConfirm} disabled={!isValid}>
            {t("common:confirm")}
          </Button>
        )}
      </Box>
    </Stack>
  );
};

export default MetricRangeSetupWizard;
